<template>
<NuxtLink
    class="internal-storefront"
    :class="['shopify', { disabled: isMaxQuantityReached }]"
    :aria-label="$t('PROMOTIONS.ADD_TO_CART')"
    tabindex="0"
    role="button"
    v-on:click="onBuyClick()"
    v-on:keydown.enter="onBuyClick()"
>
    <div class="button-details">
        <span class="icon">
            <i class="fa-duotone fa-cart-plus" />
        </span>
        <div class="buy-for-text">{{ $t('PROMOTIONS.ADD_TO_CART') }}</div>
    </div>
    <PromoPrice :product="shopifyProduct" />
</NuxtLink>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import type { Shopify } from '@jackboxgames/shopify'
import { ShoppingCart } from '$services/shopify'
import type { JBGWeb } from '$types/JBGWeb'
import PromoPrice from './PromoPrice.vue'

const props = defineProps({
    shopifyProduct: Object as PropType<Shopify.Product | null>,
    pack: Object as PropType<JBGWeb.PackDetail | null>
})

const app = useNuxtApp()
const selectedVariant = computed(() => props.shopifyProduct?.variants?.[0] || null)
const isMaxQuantityReached = computed(() => {
    const variant = selectedVariant.value

    if (!variant) {
        return false
    }

    const isSteam = ShoppingCart.isSteamVariant(variant.id)
    return isSteam && ShoppingCart.isVariantAtMaxQuantity(variant.id)
})

async function onBuyClick() {
    if (isMaxQuantityReached.value) {
        return
    }
    let variant = selectedVariant.value
    if (!variant) {
        variant = props.shopifyProduct.variants[0]
    }

    try {
        await app.$shopify.createLineItemAndAddToCart(props.shopifyProduct, variant, 1)
        ShoppingCart.open()
    } catch (error) {
        console.error('Failed to add item to cart:', error.message)
    }
}

</script>

<style lang="scss" scoped>
@use "$styles/kit.scss" as *;

.internal-storefront {
    background: linear-gradient(90deg, #9CA7FE 0%, #C0C7FF 100%);
    border: none;
    border-radius: 4px;
    color: var(--neutral-900);
    cursor: pointer;
    width: 100%;
    max-width: 252px;
    height: 40px;
    margin: 4px 0px 8px 0px;
    padding: 8px;
    border: 1px solid transparent;

    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    @media screen and (min-width: 200px) and (max-width: 439px){
        margin-right: 8px;
        min-width: 100px;
        height: 32px;
    }

    &:hover:not(:disabled) {
        background-color: linear-gradient(90deg, #8B96E6 0%, #B1BAFE 100%);
        border: 1px solid rgba(244, 245, 255, 0.80);
        box-shadow:
            0 0 16px 0 rgba(0, 0, 0, 0.10) inset,
            0 0 4px 0 rgba(176, 185, 253, 0.40),
            0 0 12px 0 rgba(166, 176, 255, 0.32),
            0 0 24px 0 rgba(206, 212, 255, 0.24);
    }

    &:active {
        background: linear-gradient(180deg, #8085a6 0%, #8F9CFF 100%);
    }

    &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
    }

    .button-details {
        display: flex;
        align-items: center;

        .buy-for-text {
            font-size: 13px;
            font-style: normal;
            margin-left: 4px;
            text-align: left;
            font-weight: 600;
            line-height: 1;
            color: var(--neutral-900);

            @media screen and (min-width: 200px) and (max-width: 439px){
                font-size: 12px;
            }
        }
    }
}
</style>
